/**
 * Platform — device/capability detection.
 *
 * UA sniffing serves the entry-wall exemption and one deliberate composition
 * fallback: a real phone/tablet keeps the mobile UI even when its reported
 * viewport is unusually large. Touch interactions still key off capabilities;
 * ordinary layout switching is viewport-driven so DevTools emulation and narrow
 * desktop windows exercise the same responsive code.
 */
/**
 * The module's surface, declared so a consumer's `Platform.isMobileUi()` is
 * typed rather than a property read on `{}` — the `let Platform = {}` shape
 * this and the sibling modules use for the Jest dependency-injection shim
 * (tests/helpers/esbuildTransformer.js) otherwise types every call site as
 * an error the client ratchet has to carry.
 */
type PlatformApi = {
  isMobileUA: () => boolean;
  isTouchPrimary: () => boolean;
  isCoarseLandscapePhone: () => boolean;
  isMobileViewport: () => boolean;
  isTabletLandscapeViewport: () => boolean;
  isMobileUi: () => boolean;
  pageSize: <T>(desktop: T, mobile: T) => T;
  syncMobileUiClass: () => boolean;
};

let Platform = {} as PlatformApi;

(function () {
  const MOBILE_UA_PATTERN = /(Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini)/i;

  // Phone-class landscape: matches the CSS battle-layout breakpoint
  // (pointer: coarse) and (orientation: landscape) and (max-height: 520px).
  const PHONE_LANDSCAPE_MAX_HEIGHT = 520;

  // Mobile-UI viewport thresholds. Viewport-driven (NOT pointer:coarse) so the
  // mobile layout also engages in a fine-pointer DevTools device emulator and
  // when a desktop window is shrunk to phone size. Desktop windows at normal
  // size never cross these, so desktop stays byte-identical.
  const MOBILE_PORTRAIT_MAX_WIDTH = 768;   // portrait phones + narrow windows
  const MOBILE_LANDSCAPE_MAX_HEIGHT = 560; // landscape phones (short viewport)

  // Landscape tablets have enough height for the two-pane Foundry workspace,
  // but not enough width for the wide desktop composition. Keep this separate
  // from isMobileViewport(): tablets must not enter the phone master/detail UI.
  //
  // THESE MIRROR THE CSS TABLET BAND and must be changed with it — the same
  // four terms appear in fourteen `@media` blocks and are guarded by
  // tests/unit/styles/tabletBandParity.test.js. When the two disagree a device
  // gets tablet STYLES and desktop PAGE SIZES, or the reverse.
  //
  // The old ceiling was 1366px wide and 8/5, which excluded an iPad Pro 13"
  // (1376px) and every iPad whose Safari tab bar pushes the aspect past 1.6 —
  // the exact devices whose battle rails were landing on the board.
  const TABLET_LANDSCAPE_MIN_WIDTH = 960;
  const TABLET_LANDSCAPE_MIN_HEIGHT = MOBILE_LANDSCAPE_MAX_HEIGHT + 1;
  const TABLET_LANDSCAPE_MAX_WIDTH = 1560;
  const TABLET_LANDSCAPE_MAX_ASPECT_RATIO = 17 / 10;

  function isMobileUA() {
    try {
      const ua = navigator.userAgent || navigator.vendor || window.opera || '';
      const isMobi = MOBILE_UA_PATTERN.test(ua);
      // iPadOS 13+ reports as desktop Mac; a touch-capable Mac is an iPad.
      const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
      return isMobi || isTouchMac;
    } catch (_) {
      return false;
    }
  }

  function isTouchPrimary() {
    try {
      return typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    } catch (_) {
      return false;
    }
  }

  function isCoarseLandscapePhone() {
    try {
      return isTouchPrimary()
        && window.innerWidth > window.innerHeight
        && Math.min(window.innerWidth, window.innerHeight) <= PHONE_LANDSCAPE_MAX_HEIGHT;
    } catch (_) {
      return false;
    }
  }

  // Phone-sized viewport in either orientation. Portrait keys off width,
  // landscape off the (short) height, mirroring the mobile.css media gate.
  function isMobileViewport() {
    try {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (!w || !h) return false;
      return h >= w
        ? w <= MOBILE_PORTRAIT_MAX_WIDTH
        : h <= MOBILE_LANDSCAPE_MAX_HEIGHT;
    } catch (_) {
      return false;
    }
  }

  function isTabletLandscapeViewport() {
    try {
      const w = Number(window.innerWidth);
      const h = Number(window.innerHeight);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return false;
      return w > h
        && w >= TABLET_LANDSCAPE_MIN_WIDTH
        && h >= TABLET_LANDSCAPE_MIN_HEIGHT
        && w <= TABLET_LANDSCAPE_MAX_WIDTH
        && (w / h) <= TABLET_LANDSCAPE_MAX_ASPECT_RATIO;
    } catch (_) {
      return false;
    }
  }

  // The single composition gate: a phone-sized viewport OR the intentional UA
  // fallback for a real mobile device that reports a large viewport. It remains
  // false for a normal-size desktop browser.
  function isMobileUi() {
    return isMobileViewport() || isMobileUA();
  }

  // Paginate-to-fit: on a phone, a smaller page keeps a grid/list inside the
  // viewport (no scrollbar) while Prev/Next reaches the rest. Returns the
  // mobile page size on a phone, the desktop size otherwise.
  function pageSize(desktop, mobile) {
    return isMobileUi() ? mobile : desktop;
  }

  // Reflect isMobileUi() onto <body> so CSS (body.mobile-ui …) and JS branch off
  // one source of truth. Returns the current boolean.
  function syncMobileUiClass() {
    const mobile = isMobileUi();
    try {
      if (document.body) document.body.classList.toggle('mobile-ui', mobile);
    } catch (_) { /* body not ready yet — the boot/resize wiring re-runs it */ }
    return mobile;
  }

  // Keep the class in sync across load, resize, and orientation changes. Wires
  // exactly once per window so repeated script evaluation cannot stack listeners.
  function watchMobileUi() {
    if (window.__moonfallMobileUiWired) return;
    window.__moonfallMobileUiWired = true;
    let debounce = null;
    // On resize/orientation change, keep the class in sync immediately, then
    // (debounced) fire moonfall:viewport-change so open paginated views can
    // re-fit their page size to the new viewport (paginate-to-fit on rotate).
    const run = () => {
      syncMobileUiClass();
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        try { window.dispatchEvent(new Event('moonfall:viewport-change')); } catch (_) { /* no-op */ }
      }, 150);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => syncMobileUiClass(), { once: true });
    } else {
      syncMobileUiClass();
    }
    window.addEventListener('resize', run, { passive: true });
    window.addEventListener('orientationchange', run, { passive: true });
  }

  Platform = {
    isMobileUA,
    isTouchPrimary,
    isCoarseLandscapePhone,
    isMobileViewport,
    isTabletLandscapeViewport,
    isMobileUi,
    pageSize,
    syncMobileUiClass,
  };

  watchMobileUi();
})();

export { Platform };
